import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Loader2, ShieldCheck, Globe2, CalendarDays, Sparkles, CheckCircle2 } from "lucide-react";
import { HYDERABAD_ADDRESS } from "@/lib/data";
import Seo from "@/components/Seo";

const SERVICE_OPTIONS = [
    "Accounting and Bookkeeping – US",
    "Building Remote Team",
];

const SCHEDULE_MAILTO =
    "mailto:info@vittaniti.com?subject=Schedule%20a%20discovery%20call&body=Hi%20Vittaniti%20team%2C%0A%0AI%27d%20like%20to%20schedule%20a%20discovery%20call.%20Here%20are%20a%20few%20time%20windows%20that%20work%20for%20me%3A%0A%0A%E2%80%A2%20%0A%E2%80%A2%20%0A%E2%80%A2%20%0A%0AThanks!";

const BACKEND_URL = "https://vittanitiservice.azurewebsites.net";
const API = `${BACKEND_URL}/api`;


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s()-]{6,}$/;

const EMPTY_FORM = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    // success: { id, status: "delivered"|"save_only"|"send_failed", message }
    const [success, setSuccess] = useState(null);

    const set = (k) => (e) => {
        setForm(f => ({ ...f, [k]: e.target.value }));
        setErrors(er => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Please enter your name.";
        if (!EMAIL_RE.test(form.email.trim())) e.email = "A valid email is required.";
        if (!PHONE_RE.test(form.phone.trim())) e.phone = "Please share a reachable phone number.";
        if (!form.service) e.service = "Pick a service.";
        if (form.message.trim().length < 10) e.message = "Tell us a little more (10+ characters).";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/contact`, form, { timeout: 15000 });
            const data = res?.data || {};
            setSuccess({
                id: data.id,
                status: data.email_status || "delivered",
                message: data.message,
            });
            setForm(EMPTY_FORM);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            // Distinguish error types so the user gets a useful message.
            const code = err?.code;
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            let msg;
            if (code === "ECONNABORTED") {
                msg = "Request timed out. Please check your connection and try again.";
            } else if (!err?.response) {
                msg = "Couldn't reach the server. Please try again in a moment.";
            } else if (status >= 500) {
                msg = "Our server hit a snag. Please try again or email us directly.";
            } else if (typeof detail === "string") {
                msg = detail;
            } else {
                msg = "Something went wrong. Please try again or email us directly.";
            }
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => setSuccess(null);

    return (
        <div data-testid="contact-page">
            <Seo
                path="/contact"
                title="Contact — Free 30-Minute Consultation"
                description="Talk to a Vittaniti partner. Book a free 30-minute discovery call to scope bookkeeping, taxation advisory or remote-team needs. NDA-ready. Response within one business day."
                keywords="contact Vittaniti, free bookkeeping consultation, schedule offshore CPA call, hire remote bookkeeper"
            />
            <section className="container-page pt-16 md:pt-24 pb-12">
                <div className="flex items-center gap-3 mb-6">
                    <span className="ornament w-12" />
                    <p className="overline">Get in touch</p>
                    <span aria-hidden="true" className="eyebrow-dot" />
                </div>
                <h1 className="display text-5xl md:text-6xl lg:text-7xl text-brand-ink max-w-4xl">
                    Let&apos;s start with a <span className="italic text-brand-amber">free</span> consultation<span className="brand-dot">.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-brand-ink/70 leading-relaxed">
                    Tell us a little about your business and we&apos;ll respond within one business day —
                    and usually much faster. No bots, just our team.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-brand-muted" data-testid="contact-trust-badges">
                    <span className="inline-flex items-center gap-1.5">
                        <Globe2 size={14} className="text-brand-amber" />
                        Serving clients across the US &amp; Europe
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <ShieldCheck size={14} className="text-brand-amber" />
                        Data compliant &amp; NDA ready
                    </span>
                </div>
            </section>

            <section className="container-page pb-24">
                <div className="grid lg:grid-cols-5 gap-10">
                    {/* Form */}
                    <div className="lg:col-span-3 space-y-6">
                        {success ? (
                            <div className="card-3d p-8 md:p-10" data-testid="contact-success-card">
                                <span className="grid place-items-center w-14 h-14 rounded-2xl bg-brand-creamLight text-brand-amber">
                                    <CheckCircle2 size={28} strokeWidth={1.7} />
                                </span>
                                <h2 className="display text-3xl md:text-4xl text-brand-ink mt-6 leading-tight">
                                    Message received<span className="brand-dot">.</span>
                                </h2>
                                <p className="mt-4 text-brand-ink/80 leading-relaxed">
                                    Thanks for reaching out — a Vittaniti partner will review your
                                    enquiry and reply within one business day, usually much sooner.
                                </p>
                                <p className="mt-3 text-sm text-brand-muted">
                                    A confirmation email is on its way to your inbox. If you don&apos;t
                                    see it within a few minutes, please check your spam folder.
                                </p>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <button
                                        type="button"
                                        onClick={reset}
                                        data-testid="contact-send-another"
                                        className="btn-ghost"
                                    >
                                        Send another message
                                    </button>
                                    <a href={SCHEDULE_MAILTO} className="btn-primary" data-testid="contact-success-schedule">
                                        <CalendarDays size={16} /> Book a discovery call
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <>
                        <div className="card-3d p-5 md:p-6 flex items-start gap-4" data-testid="contact-pre-consult">
                            <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-creamLight text-brand-amber shrink-0">
                                <Sparkles size={18} strokeWidth={1.6} />
                            </span>
                            <div>
                                <p className="overline mb-1">What to expect</p>
                                <p className="text-sm text-brand-ink/80 leading-relaxed">
                                    Before our call, we&apos;ll review your enquiry to ensure we can
                                    provide immediate value. No high-pressure sales — just a candid
                                    discussion about your current roadblocks and how we might help.
                                </p>
                            </div>
                        </div>

                        <form onSubmit={onSubmit} className="card-3d p-7 md:p-10" data-testid="contact-form" noValidate>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Your name" id="name" error={errors.name}>
                                <input id="name" type="text" autoComplete="name"
                                       value={form.name} onChange={set("name")}
                                       data-testid="contact-input-name"
                                       className={inputCls(errors.name)} placeholder="e.g. Aravind Nair" />
                            </Field>
                            <Field label="Email" id="email" error={errors.email}>
                                <input id="email" type="email" autoComplete="email"
                                       value={form.email} onChange={set("email")}
                                       data-testid="contact-input-email"
                                       className={inputCls(errors.email)} placeholder="you@business.com" />
                            </Field>
                            <Field label="Phone" id="phone" error={errors.phone}>
                                <input id="phone" type="tel" autoComplete="tel"
                                       value={form.phone} onChange={set("phone")}
                                       data-testid="contact-input-phone"
                                       className={inputCls(errors.phone)} placeholder="+91 91234 56789" />
                            </Field>
                            <Field label="Service of interest" id="service" error={errors.service}>
                                <select id="service" value={form.service} onChange={set("service")}
                                        data-testid="contact-input-service"
                                        className={inputCls(errors.service)}>
                                    <option value="">Choose a service…</option>
                                    {SERVICE_OPTIONS.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                    <option value="Not sure yet">Not sure yet — please advise</option>
                                </select>
                            </Field>
                        </div>
                        <div className="mt-5">
                            <Field label="How can we help?" id="message" error={errors.message}>
                                <textarea id="message" rows={6}
                                          value={form.message} onChange={set("message")}
                                          data-testid="contact-input-message"
                                          className={inputCls(errors.message) + " resize-y"}
                                          placeholder="Share a few sentences about your business and what you need." />
                            </Field>
                        </div>
                        <div className="mt-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <p className="text-xs text-brand-muted max-w-sm">
                                By submitting, you agree to be contacted by Vittaniti. We don&apos;t share your details.
                            </p>
                            <button type="submit" disabled={submitting}
                                    data-testid="contact-submit-button"
                                    className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed">
                                {submitting ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>) : "Send enquiry"}
                            </button>
                        </div>
                        </form>

                        <a
                            href={SCHEDULE_MAILTO}
                            data-testid="contact-direct-schedule"
                            className="card-3d p-5 md:p-6 flex items-center justify-between gap-4 hover:bg-brand-creamLight transition-colors"
                        >
                            <span className="flex items-center gap-4">
                                <span className="grid place-items-center w-10 h-10 rounded-xl bg-brand-ink text-white shrink-0">
                                    <CalendarDays size={18} strokeWidth={1.6} />
                                </span>
                                <span>
                                    <span className="block text-sm font-semibold text-brand-ink">Prefer to book a time directly?</span>
                                    <span className="block text-xs text-brand-muted">Email us your preferred slots — we&apos;ll confirm a calendar invite within hours.</span>
                                </span>
                            </span>
                            <span className="text-xs uppercase tracking-[0.18em] text-brand-amber font-semibold">Email →</span>
                        </a>
                            </>
                        )}
                    </div>

                    {/* Info */}
                    <aside className="lg:col-span-2 space-y-6">
                        <div className="card-3d p-7">
                            <p className="overline mb-5">Visit us</p>
                            <ul className="space-y-4 text-sm">
                                <li className="flex gap-3"><MapPin size={18} className="text-brand-amber shrink-0 mt-0.5" />
                                    <span className="text-brand-ink/85">
                                        {HYDERABAD_ADDRESS.line1}, {HYDERABAD_ADDRESS.line2},<br />{HYDERABAD_ADDRESS.city}, {HYDERABAD_ADDRESS.country}
                                    </span></li>
                                <li className="flex items-center gap-3"><Phone size={18} className="text-brand-amber shrink-0" />
                                    <a href={HYDERABAD_ADDRESS.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-phone" className="text-brand-ink/85 link-underline">{HYDERABAD_ADDRESS.phone}</a></li>
                                <li className="flex items-center gap-3"><Mail size={18} className="text-brand-amber shrink-0" />
                                    <a href={`mailto:${HYDERABAD_ADDRESS.email}`} data-testid="contact-email" className="text-brand-ink/85 link-underline">{HYDERABAD_ADDRESS.email}</a></li>
                                <li className="flex items-center gap-3"><Clock size={18} className="text-brand-amber shrink-0" />
                                    <span className="text-brand-ink/85">{HYDERABAD_ADDRESS.hours}</span></li>
                            </ul>
                        </div>

                        <div className="card-3d overflow-hidden">
                            <iframe
                                src={HYDERABAD_ADDRESS.mapsEmbed}
                                title="Vittaniti Hyderabad location"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="w-full h-72 border-0"
                                data-testid="contact-map"
                            />
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}

function Field({ label, id, error, children }) {
    return (
        <label htmlFor={id} className="block">
            <span className="overline block mb-2 text-brand-muted">{label}</span>
            {children}
            {error && <span className="block mt-1.5 text-xs text-red-700" data-testid={`error-${id}`}>{error}</span>}
        </label>
    );
}

function inputCls(error) {
    return `w-full bg-white border ${error ? "border-red-400" : "border-brand-border"} rounded-lg px-4 py-3 text-sm text-brand-ink placeholder:text-brand-muted/70 focus:outline-none focus:ring-2 focus:ring-brand-ink/20 focus:border-brand-ink transition`;
}
